import Logger from '../lib/logger';
import event, { type EventEmitter } from '../lib/mixins/event';

/**
 * RotatingHeadline - vertical "slot machine" headline.
 *
 * The items are stacked in a column track inside a fixed-height window
 * (--height, set in CSS). Advancing just bumps the `--rotating-index` custom
 * property; CSS handles the translateY slide via a transition, so the animation
 * runs on the compositor with no per-frame JS. (The previous GSAP version kept
 * a requestAnimationFrame ticker alive, which held the whole third-party
 * analytics stack awake every frame — see the CSS-marquee conversion.)
 *
 * A clone of the first item is appended so the last -> first wrap slides
 * forward seamlessly instead of rewinding.
 */
/** Object.assign below grafts the event mixin onto the prototype at runtime. */
interface RotatingHeadline extends EventEmitter {}

class RotatingHeadline {

	NAME: string;
	dom: HTMLElement;
	track: HTMLElement | null;
	items: Element[];
	count: number;
	index: number;

	constructor(dom: HTMLElement) {
		this.NAME = 'RotatingHeadline';

		this.dom = dom;
		this.track = this.dom.querySelector<HTMLElement>('[data-rotating-headline-items]');
		this.items = this.track
			? Array.from(this.track.querySelectorAll('[data-rotating-headline-item]'))
			: [];
		this.count = this.items.length;
		this.index = 0;
	}

	init() {
		Logger.info(`${this.NAME} > init`);

		if (this.count <= 1 || !this.track) return;

		// Respect reduced motion: leave the first headline shown, don't rotate.
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

		// Hide the wrap clone from assistive tech so the first headline isn't
		// announced twice (matches the aria-hidden duplicates in the CSS marquees).
		const clone = this.items[0].cloneNode(true) as Element;
		clone.setAttribute('aria-hidden', 'true');
		this.track.appendChild(clone);
		this.track.addEventListener('transitionend', () => this.handleWrap());

		// Advances ~0.5x/sec, so there's no offscreen-pause gating: the mutation
		// rate is negligible (unlike the old per-frame GSAP marquees this replaced).
		setInterval(() => this.advance(), 2000);
	}

	advance() {
		this.index++;
		this.track?.style.setProperty('--rotating-index', String(this.index));
	}

	// After sliding onto the appended clone of the first item, snap back to the
	// real first item with the transition suppressed so the next advance
	// continues forward without a visible rewind.
	handleWrap() {
		if (this.index < this.count || !this.track) return;

		this.index = 0;
		this.track.style.transition = 'none';
		this.track.style.setProperty('--rotating-index', '0');
		void this.track.offsetHeight; // force reflow so the snap isn't animated
		this.track.style.transition = '';
	}
}

Object.assign(RotatingHeadline.prototype, event);

export default RotatingHeadline;
